import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Clock,
    Calculator,
    GitBranch,
    Percent,
    Repeat2,
    Car,
    Calendar,
    TrendingUp,
    Smartphone,
    ShieldCheck,
    ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import LeadForm from '../../components/public/LeadForm';
import BrowserChrome from '../../components/public/BrowserChrome';
import PublicFooter from '../../components/public/PublicFooter';

const LOGO_URL = `${process.env.PUBLIC_URL}/hisland-logo.png`;

const FEATURES = [
    { key: 'tax', Icon: Calculator },
    { key: 'fortnox', Icon: GitBranch },
    { key: 'flex', Icon: Clock },
];

// 8 highlight cards — matches landing.highlights.items keys
const HIGHLIGHTS = [
    { key: 'tax', Icon: Percent },
    { key: 'fortnox', Icon: GitBranch },
    { key: 'mileage', Icon: Car },
    { key: 'flex', Icon: Repeat2 },
    { key: 'calendar', Icon: Calendar },
    { key: 'margin', Icon: TrendingUp },
    { key: 'pwa', Icon: Smartphone },
    { key: 'gdpr', Icon: ShieldCheck },
];

export default function LandingPage() {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.startsWith('sv') ? 'sv' : 'en';

    const toggleLanguage = () => {
        const next = currentLang === 'sv' ? 'en' : 'sv';
        i18n.changeLanguage(next);
    };

    // Path used in the hero BrowserChrome URL bar
    const heroUrlPath = '/dashboard/log-reports';

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            {/* ── Top Navigation ─────────────────────────────────────── */}
            <header className="border-b border-border sticky top-0 z-40 bg-background">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Hisland"
                            className="h-7 w-auto"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="font-heading font-bold tracking-widest text-sm uppercase">
                            HISLAND TIME
                        </span>
                    </div>

                    {/* Right nav */}
                    <nav className="flex items-center gap-4">
                        <button
                            onClick={toggleLanguage}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider"
                            aria-label={t('landing.nav.lang_toggle')}
                        >
                            {currentLang === 'sv' ? 'EN' : 'SV'}
                        </button>
                        <Link
                            to="/login"
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {t('landing.nav.login')}
                        </Link>
                    </nav>
                </div>
            </header>

            <main className="flex-1">
                {/* ── Hero ───────────────────────────────────────────── */}
                <section className="border-b border-border bg-card">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                        <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight leading-tight max-w-2xl">
                            {t('landing.hero.headline')}
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            {t('landing.hero.sub')}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a href="#contact">
                                <Button size="lg" className="uppercase tracking-widest text-sm font-semibold">
                                    {t('landing.hero.cta')}
                                </Button>
                            </a>
                            <Link
                                to="/features"
                                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                            >
                                {t('landing.highlights.cta')}
                                <ArrowRight className="size-3.5" aria-hidden="true" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ── Feature Differentiators (3 cards) ──────────────── */}
                <section className="border-b border-border">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                        <div className="grid gap-6 sm:grid-cols-3">
                            {FEATURES.map(({ key, Icon }) => (
                                <Card key={key} className="border border-border bg-card">
                                    <CardHeader className="pb-3">
                                        <div className="mb-3">
                                            <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
                                        </div>
                                        <CardTitle className="text-base font-semibold font-heading uppercase tracking-wider">
                                            {t(`landing.features.${key}.title`)}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {t(`landing.features.${key}.body`)}
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Hero Screenshot ─────────────────────────────────── */}
                <section className="border-b border-border bg-card">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                        <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-center mb-2">
                            {t('landing.highlights.heading')}
                        </h2>
                        <p className="text-sm text-muted-foreground text-center mb-10">
                            {t('landing.highlights.sub')}
                        </p>
                        <div
                            className="mx-auto"
                            style={{
                                transform: 'perspective(1200px) rotateY(-3deg)',
                                transformOrigin: 'center center',
                            }}
                        >
                            <BrowserChrome urlPath={heroUrlPath}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/screenshots/hero-timesheet-calendar.png`}
                                    alt={t('landing.highlights.hero_screenshot_alt')}
                                    className="w-full h-auto block"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </BrowserChrome>
                        </div>
                    </div>
                </section>

                {/* ── Highlights Grid ──────────────────────────────────── */}
                <section className="border-b border-border">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                        <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-2">
                            {t('landing.highlights.section_title')}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-10">
                            {t('landing.highlights.section_sub')}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {HIGHLIGHTS.map(({ key, Icon }) => (
                                <div
                                    key={key}
                                    className="border border-border rounded-lg p-4 bg-card hover:border-foreground/20 transition-colors"
                                >
                                    <Icon className="size-4 text-muted-foreground mb-3" aria-hidden="true" />
                                    <p className="text-sm font-semibold leading-tight mb-1">
                                        {t(`landing.highlights.items.${key}.title`)}
                                    </p>
                                    <p className="text-xs text-muted-foreground leading-snug">
                                        {t(`landing.highlights.items.${key}.sub`)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* See all features CTA */}
                        <div className="mt-12 text-center">
                            <Link to="/features">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="uppercase tracking-widest text-sm font-semibold gap-2"
                                >
                                    {t('landing.highlights.cta')}
                                    <ArrowRight className="size-4" aria-hidden="true" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ── Pilot Framing ────────────────────────────────────── */}
                <section className="border-b border-border">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
                        <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
                            {t('landing.pilot.body')}
                        </p>
                    </div>
                </section>

                {/* ── Lead-Capture Form ─────────────────────────────────── */}
                <section id="contact" className="border-b border-border bg-card">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
                        <LeadForm />
                    </div>
                </section>
            </main>

            <PublicFooter />
        </div>
    );
}
